import { getCookie, setCookie } from '../utils/cookie-functions';

let interval;
let gpcApplied = false;
let userInteracted = false;

// Use a constant for the category code
const TARGETING_CATEGORY = 'C0004';

// Intercept cookie and localStorage writes to prevent OneTrust from permanently hiding the banner
const interceptStorage = () => {
  if (typeof window === 'undefined' || window.__thinx_storage_intercepted) return;
  window.__thinx_storage_intercepted = true;

  try {
    const originalCookieSetter = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie').set;
    const originalCookieGetter = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie').get;

    Object.defineProperty(document, 'cookie', {
      set(val) {
        let modifiedVal = val;
        if (!userInteracted && navigator.globalPrivacyControl) {
          if (typeof modifiedVal === 'string' && modifiedVal.includes('OptanonAlertBoxClosed')) {
            return; // Block banner dismissal cookie completely
          }
          if (typeof modifiedVal === 'string' && modifiedVal.includes('OptanonConsent')) {
            modifiedVal = modifiedVal.replace(/(OptanonConsent=[^;]*)/, (match) => {
              let m = match;
              if (m.match(/interactionCount(?:=|%3D)[0-9]+/)) {
                m = m.replace(/(interactionCount(?:=|%3D))[0-9]+/, '$10');
              } else {
                const sep = m.includes('%3D') ? '%26' : '&';
                const eq = m.includes('%3D') ? '%3D' : '=';
                m += `${sep}interactionCount${eq}0`;
              }
              m = m.replace(/(isGpcEnabled(?:=|%3D))1/, '$10');
              return m;
            });
          }
        }
        originalCookieSetter.call(this, modifiedVal);
      },
      get() {
        return originalCookieGetter.call(this);
      },
    });

    const originalSetItem = Storage.prototype.setItem;
    Storage.prototype.setItem = function setItem(key, value) {
      let modifiedValue = value;
      if (!userInteracted && navigator.globalPrivacyControl) {
        if (key === 'OptanonAlertBoxClosed') {
          return; // Block banner dismissal localStorage completely
        }
        if (key === 'OptanonConsent' && typeof modifiedValue === 'string') {
          if (modifiedValue.match(/interactionCount(?:=|%3D)[0-9]+/)) {
            modifiedValue = modifiedValue.replace(/(interactionCount(?:=|%3D))[0-9]+/, '$10');
          } else {
            const sep = modifiedValue.includes('%3D') ? '%26' : '&';
            const eq = modifiedValue.includes('%3D') ? '%3D' : '=';
            modifiedValue += `${sep}interactionCount${eq}0`;
          }
          modifiedValue = modifiedValue.replace(/(isGpcEnabled(?:=|%3D))1/, '$10');
        }
      }
      originalSetItem.call(this, key, modifiedValue);
    };
  } catch (e) {
    // Ignore interception errors
  }
};

if (typeof document !== 'undefined') {
  userInteracted = !!getCookie('thinx_user_interacted');
  interceptStorage();

  // Track if the user explicitly interacts with the banner
  document.addEventListener(
    'click',
    (e) => {
      const inOneTrust = e.target.closest('#onetrust-consent-sdk') || e.target.closest('#onetrust-banner-sdk');
      if (inOneTrust && (e.target.tagName === 'BUTTON' || e.target.closest('button'))) {
        userInteracted = true;
        setCookie('thinx_user_interacted', 'true', 365);
      }
    },
    true,
  );
}

const forceDeleteCookie = (name) => {
  const { hostname } = window.location;
  const parts = hostname.split('.');
  const domains = [hostname, `.${hostname}`];
  if (parts.length > 2) {
    domains.push(parts.slice(-2).join('.'));
    domains.push(`.${parts.slice(-2).join('.')}`);
  }

  domains.forEach((d) => {
    const base = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    document.cookie = `${base}; domain=${d}`;
    document.cookie = `${base}; domain=${d}; Secure`;
    document.cookie = `${base}; domain=${d}; SameSite=None; Secure`;
    document.cookie = `${base}; domain=${d}; SameSite=Lax`;
    document.cookie = `${base}`;
    document.cookie = `${base}; Secure`;
    document.cookie = `${base}; SameSite=None; Secure`;
    document.cookie = `${base}; SameSite=Lax`;
  });
};

const forceSetCookie = (name, value) => {
  const { hostname } = window.location;
  const parts = hostname.split('.');
  const domains = [hostname, `.${hostname}`];
  if (parts.length > 2) {
    domains.push(parts.slice(-2).join('.'));
    domains.push(`.${parts.slice(-2).join('.')}`);
  }

  domains.forEach((d) => {
    document.cookie = [
      `${name}=${value}`,
      'expires=Fri, 31 Dec 2038 23:59:59 GMT',
      'path=/',
      `domain=${d}`,
      'SameSite=Lax',
    ].join('; ');
    document.cookie = [
      `${name}=${value}`,
      'expires=Fri, 31 Dec 2038 23:59:59 GMT',
      'path=/',
      `domain=${d}`,
      'SameSite=None',
      'Secure',
    ].join('; ');
  });
};

const forceCleanOneTrust = () => {
  if (userInteracted) return;

  forceDeleteCookie('OptanonAlertBoxClosed');

  let consent = getCookie('OptanonConsent');
  if (consent) {
    let modified = false;
    if (consent.match(/interactionCount(?:=|%3D)[1-9][0-9]*/)) {
      consent = consent.replace(/(interactionCount(?:=|%3D))[0-9]+/, '$10');
      modified = true;
    } else if (!consent.match(/interactionCount(?:=|%3D)/)) {
      const separator = consent.includes('%3D') ? '%26' : '&';
      const equals = consent.includes('%3D') ? '%3D' : '=';
      consent += `${separator}interactionCount${equals}0`;
      modified = true;
    }

    if (consent.match(/isGpcEnabled(?:=|%3D)1/)) {
      consent = consent.replace(/(isGpcEnabled(?:=|%3D))1/, '$10');
      modified = true;
    }

    if (modified) forceSetCookie('OptanonConsent', consent);
  }

  try {
    localStorage.removeItem('OptanonAlertBoxClosed');
    let lsConsent = localStorage.getItem('OptanonConsent');
    if (lsConsent) {
      let lsModified = false;
      if (lsConsent.match(/interactionCount(?:=|%3D)[1-9][0-9]*/)) {
        lsConsent = lsConsent.replace(/(interactionCount(?:=|%3D))[0-9]+/, '$10');
        lsModified = true;
      } else if (!lsConsent.match(/interactionCount(?:=|%3D)/)) {
        const separator = lsConsent.includes('%3D') ? '%26' : '&';
        const equals = lsConsent.includes('%3D') ? '%3D' : '=';
        lsConsent += `${separator}interactionCount${equals}0`;
        lsModified = true;
      }

      if (lsConsent.match(/isGpcEnabled(?:=|%3D)1/)) {
        lsConsent = lsConsent.replace(/(isGpcEnabled(?:=|%3D))1/, '$10');
        lsModified = true;
      }

      if (lsModified) localStorage.setItem('OptanonConsent', lsConsent);
    }
  } catch (e) {
    // ignore
  }
};

const safeUpdateConsent = (category, value, retries = 3) => {
  try {
    if (window.OneTrust?.UpdateConsent) {
      // Clean existing state first just in case
      forceCleanOneTrust();

      window.OneTrust.UpdateConsent('Category', `${category}:${value}`);

      // Because we intercepted document.cookie and localStorage writes,
      // UpdateConsent will be sandboxed and won't successfully save the banner-hiding state!

      if (!userInteracted) {
        // Run a brief interval to catch any asynchronous writes and force CSS visibility
        let attempts = 0;
        const cleanInterval = setInterval(() => {
          forceCleanOneTrust();
          attempts += 1;
          if (attempts > 15) clearInterval(cleanInterval);
        }, 100);
      }
    } else if (retries > 0) {
      setTimeout(() => safeUpdateConsent(category, value, retries - 1), 800);
    }
  } catch {
    if (retries > 0) setTimeout(() => safeUpdateConsent(category, value, retries - 1), 800);
  }
};

const enforceBannerVisibility = () => {
  let observer = null;

  const setupObserver = (banner) => {
    if (observer) return;

    const enforceVisible = () => {
      if (!userInteracted) {
        banner.style.setProperty('display', 'block', 'important');
        banner.style.setProperty('opacity', '1', 'important');
        banner.style.setProperty('visibility', 'visible', 'important');
        banner.style.setProperty('pointer-events', 'auto', 'important');
        banner.style.setProperty('height', 'auto', 'important');
      }
    };

    if (!userInteracted) enforceVisible();

    observer = new MutationObserver(() => {
      if (banner.style.display === 'none' && !userInteracted) {
        enforceVisible();
      }
    });

    observer.observe(banner, { attributes: true, attributeFilter: ['style', 'class'] });
  };

  const banner = document.getElementById('onetrust-banner-sdk');
  if (banner) {
    setupObserver(banner);
  } else {
    const bodyObserver = new MutationObserver((mutations, obs) => {
      const delayedBanner = document.getElementById('onetrust-banner-sdk');
      if (delayedBanner) {
        obs.disconnect();
        setupObserver(delayedBanner);
      }
    });
    bodyObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
  }
};

const detectGPC = () => {
  enforceBannerVisibility();
  return navigator.globalPrivacyControl === true;
};

export const setGpcOneTrust = () => {
  if (gpcApplied) return;
  if (!detectGPC()) return;
  if (!window.OneTrust) return;

  const isReady =
    typeof window.OneTrust.UpdateConsent === 'function' && typeof window.OnetrustActiveGroups !== 'undefined';

  if (!isReady) return;

  const domainDataReady = !!(window.OneTrust.GetDomainData && window.OneTrust.GetDomainData());
  const activeGroupsReady = typeof window.OnetrustActiveGroups !== 'undefined';
  if (!domainDataReady && !activeGroupsReady) return;

  safeUpdateConsent(TARGETING_CATEGORY, 0);

  window.OneTrust.gpcEnabled = true;
  gpcApplied = true;

  if (!getCookie('limittheuse')) setCookie('limittheuse', true, 365);
  if (!getCookie('donotsell')) setCookie('donotsell', true, 365);

  clearInterval(interval);
};

const getConsentGroups = () => {
  const shopifyConsentCookie = getCookie('_tracking_consent');
  let shopifyConsentCookieJSON = null;
  try {
    if (shopifyConsentCookie) {
      shopifyConsentCookieJSON = JSON.parse(decodeURIComponent(shopifyConsentCookie));
    }
  } catch {
    // ignore
  }

  const shopifyConsentGroups = shopifyConsentCookieJSON?.cus?.['onetrust.groups'];
  const optanonConsentGroups = window.OnetrustActiveGroups;
  return { shopifyConsentGroups, optanonConsentGroups };
};

export const overrideDNS = () => {
  if (!window.OneTrust || !gpcApplied) return;

  const { shopifyConsentGroups, optanonConsentGroups } = getConsentGroups();
  const isLimitTheUse = getCookie('limittheuse');
  const isDoNotSell = getCookie('donotsell');

  const shouldOverride =
    (isLimitTheUse || isDoNotSell) &&
    (shopifyConsentGroups?.includes(`${TARGETING_CATEGORY}:1`) || optanonConsentGroups?.includes(TARGETING_CATEGORY));

  if (shouldOverride) {
    if (!sessionStorage.getItem('gpc_reloaded')) {
      sessionStorage.setItem('gpc_reloaded', 'true');
      setTimeout(() => {
        safeUpdateConsent(TARGETING_CATEGORY, 0);
        const checkInterval = setInterval(() => {
          const { shopifyConsentGroups: updated } = getConsentGroups();
          if (updated?.includes(`${TARGETING_CATEGORY}:0`)) {
            clearInterval(checkInterval);
            window.location.reload();
          }
        }, 250);
      }, 1500);
    } else {
      safeUpdateConsent(TARGETING_CATEGORY, 0);
    }
  }

  clearInterval(interval);
};

function initConsentManager() {
  const attempt = () => {
    if (!window.OneTrust) return;
    if (detectGPC()) {
      setGpcOneTrust();
      overrideDNS();
    }
  };
  interval = setInterval(attempt, 800);
  attempt();
  document.addEventListener('OneTrustLoaded', attempt);
}

function GPCBanner() {
  const banner = document.getElementById('gpcBanner');
  if (!banner) return;

  const COOKIE_NAME = 'gpcBannerDismissed';

  const gpcEnabled = navigator.globalPrivacyControl === true;

  const dismissed = document.cookie.split('; ').some((row) => row.startsWith(`${COOKIE_NAME}=true`));

  if (gpcEnabled && !dismissed) {
    banner.classList.remove('hidden');
  }

  const closeBtn = banner.querySelector('.gpc-close');
  if (!closeBtn) return;

  closeBtn.addEventListener('click', () => {
    document.cookie = `${COOKIE_NAME}=true; path=/; SameSite=Lax`;
    banner.classList.add('hidden');
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initConsentManager();
  GPCBanner();
});
